// Estados, prioridades y reglas de cumplimiento de los proyectos.
// Los permisos replican los del backend (modules/proyectos/service.py), que es quien los aplica.
import { hoyISO } from "../../shared/estilos";

const gris = "bg-zinc-500/15 text-zinc-300 border-zinc-500/30";
const azul = "bg-sky-500/15 text-sky-300 border-sky-500/30";
const ambar = "bg-amber-500/15 text-amber-300 border-amber-500/30";
const verde = "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
const rojo = "bg-rose-500/15 text-rose-300 border-rose-500/30";

export const ESTADOS_TAREA = [
  { id: "pendiente", label: "Por hacer", clase: gris, punto: "bg-zinc-400" },
  { id: "en_progreso", label: "En progreso", clase: azul, punto: "bg-sky-400" },
  { id: "en_revision", label: "En revisión", clase: ambar, punto: "bg-amber-400" },
  { id: "completada", label: "Hecho", clase: verde, punto: "bg-emerald-400" },
];

export const ESTADOS_PROYECTO = [
  { id: "planeado", label: "Planeado", clase: gris },
  { id: "en_curso", label: "En curso", clase: azul },
  { id: "pausado", label: "Pausado", clase: ambar },
  { id: "completado", label: "Completado", clase: verde },
  { id: "cancelado", label: "Cancelado", clase: rojo },
];

export const PRIORIDADES = [
  { id: "baja", label: "Baja", rango: 0, clase: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  { id: "media", label: "Media", rango: 1, clase: azul },
  { id: "alta", label: "Alta", rango: 2, clase: ambar },
  { id: "urgente", label: "Urgente", rango: 3, clase: rojo },
];

export const SALUD = {
  ok: { label: "Al día", clase: verde },
  riesgo: { label: "En riesgo", clase: ambar },
  atrasado: { label: "Atrasado", clase: rojo },
  cerrado: { label: "Cerrado", clase: gris },
};

export const datosDe = (lista, id) => lista.find((x) => x.id === id) || lista[0];

export const nombrePersona = (persona) =>
  persona ? `${persona.nombres || ""} ${persona.apellidos || ""}`.trim() : "";

// ─── Permisos ─────────────────────────────────────────────────

export const esAdministrador = (usuario) => Boolean(usuario?.acceso?.total);

export const lidera = (usuario, proyecto) =>
  Boolean(proyecto && usuario?.miembro_id && proyecto.lider_id === usuario.miembro_id);

export const gestiona = (usuario, proyecto) => esAdministrador(usuario) || lidera(usuario, proyecto);

export const esResponsable = (usuario, tarea) =>
  Boolean(tarea && usuario?.miembro_id && tarea.responsable_id === usuario.miembro_id);

// ─── Fechas y cumplimiento ────────────────────────────────────

const dia = (s) => new Date(`${String(s).slice(0, 10)}T00:00:00`);
const diasEntre = (desde, hasta) => Math.round((dia(hasta) - dia(desde)) / 86400000);
const diaLocal = (iso) => new Date(iso).toLocaleDateString("en-CA");

export const fechaCorta = (s) => {
  if (!s) return "—";
  const fecha = dia(s);
  const opciones = { day: "numeric", month: "short" };
  if (fecha.getFullYear() !== new Date().getFullYear()) opciones.year = "numeric";
  return fecha.toLocaleDateString("es-CO", opciones);
};

export const fechaHora = (iso) =>
  iso ? new Date(iso).toLocaleString("es-CO", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "";

export const estaHecha = (tarea) => tarea.estado === "completada";

export const estaVencida = (tarea) => !estaHecha(tarea) && Boolean(tarea.fecha_limite) && tarea.fecha_limite < hoyISO();

// true / false si se completó antes o después de su fecha límite; null si no aplica.
export const aTiempo = (tarea) =>
  estaHecha(tarea) && tarea.fecha_limite && tarea.completada_en ? diaLocal(tarea.completada_en) <= tarea.fecha_limite : null;

export function vencimiento(tarea) {
  if (estaHecha(tarea)) {
    if (!tarea.completada_en) return { texto: "Hecho", clase: "text-emerald-300" };
    return aTiempo(tarea) === false
      ? { texto: "Hecho con retraso", clase: "text-amber-300" }
      : { texto: `Hecho ${fechaCorta(diaLocal(tarea.completada_en))}`, clase: "text-emerald-300" };
  }
  if (!tarea.fecha_limite) return { texto: "Sin fecha", clase: "text-gray-500" };
  const dias = diasEntre(hoyISO(), tarea.fecha_limite);
  if (dias < 0) return { texto: `Vencida hace ${-dias} ${dias === -1 ? "día" : "días"}`, clase: "text-rose-400" };
  if (dias === 0) return { texto: "Vence hoy", clase: "text-amber-300" };
  if (dias === 1) return { texto: "Vence mañana", clase: "text-amber-300" };
  if (dias <= 7) return { texto: `Vence en ${dias} días`, clase: dias <= 2 ? "text-amber-300" : "text-gray-400" };
  return { texto: `Vence ${fechaCorta(tarea.fecha_limite)}`, clase: "text-gray-400" };
}

// Hechas primero al final; las demás por fecha límite y, a igual fecha, por prioridad.
export function ordenarTareas(tareas) {
  const rango = (t) => datosDe(PRIORIDADES, t.prioridad).rango;
  return [...tareas].sort((a, b) => {
    if (estaHecha(a) !== estaHecha(b)) return estaHecha(a) ? 1 : -1;
    if (a.fecha_limite !== b.fecha_limite) {
      if (!a.fecha_limite) return 1;
      if (!b.fecha_limite) return -1;
      return a.fecha_limite.localeCompare(b.fecha_limite);
    }
    return rango(b) - rango(a);
  });
}

export function avance(total, hechas, estado) {
  if (!total) return estado === "completado" ? 100 : 0;
  return Math.round((hechas / total) * 100);
}

// Avance que debería llevar según el tiempo transcurrido (0–100), o null sin fechas.
export function avanceEsperado(proyecto) {
  const { fecha_inicio: inicio, fecha_fin: fin } = proyecto;
  if (!inicio || !fin) return null;
  const duracion = diasEntre(inicio, fin);
  if (duracion <= 0) return hoyISO() >= fin ? 100 : 0;
  return Math.min(100, Math.max(0, (diasEntre(inicio, hoyISO()) / duracion) * 100));
}

// "tareasVencidas": si alguna tarea abierta ya pasó su fecha límite.
export function salud(proyecto, avanceActual, tareasVencidas = false) {
  if (proyecto.estado === "completado" || proyecto.estado === "cancelado") return "cerrado";
  if (proyecto.fecha_fin && proyecto.fecha_fin < hoyISO() && avanceActual < 100) return "atrasado";
  const esperado = avanceEsperado(proyecto);
  if ((esperado !== null && avanceActual < esperado - 15) || tareasVencidas) return "riesgo";
  return "ok";
}

export const tamanoArchivo = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Etiqueta y color del cuadro que reemplaza la miniatura de un documento que no es imagen.
const TIPOS_ARCHIVO = [
  [["pdf"], "PDF", "bg-rose-500/20 text-rose-300"],
  [["doc", "docx", "odt", "rtf"], "DOC", "bg-sky-500/20 text-sky-300"],
  [["xls", "xlsx", "ods", "csv"], "XLS", "bg-emerald-500/20 text-emerald-300"],
  [["ppt", "pptx", "odp", "key"], "PPT", "bg-amber-500/20 text-amber-300"],
  [["jpg", "jpeg", "png", "gif", "webp", "heic", "svg"], "IMG", "bg-violet-500/20 text-violet-300"],
  [["mp3", "wav", "m4a", "ogg"], "AUD", "bg-fuchsia-500/20 text-fuchsia-300"],
  [["mp4", "mov", "avi", "webm"], "VID", "bg-fuchsia-500/20 text-fuchsia-300"],
  [["zip", "rar", "7z"], "ZIP", "bg-zinc-500/20 text-zinc-300"],
];

export function tipoArchivo(nombre) {
  const extension = (String(nombre).split(".").pop() || "").toLowerCase();
  const tipo = TIPOS_ARCHIVO.find(([extensiones]) => extensiones.includes(extension));
  return tipo
    ? { texto: tipo[1], clase: tipo[2] }
    : { texto: (extension || "doc").slice(0, 4).toUpperCase(), clase: "bg-zinc-500/20 text-zinc-300" };
}

// Reduce una foto (máx. `lado` px por lado, JPEG) antes de subirla, como el portal.
export function reducirImagen(archivo, lado = 1600) {
  return new Promise((resolver, rechazar) => {
    const imagen = new Image();
    const url = URL.createObjectURL(archivo);
    imagen.onload = () => {
      const escala = Math.min(1, lado / Math.max(imagen.width, imagen.height));
      const lienzo = document.createElement("canvas");
      lienzo.width = Math.round(imagen.width * escala);
      lienzo.height = Math.round(imagen.height * escala);
      lienzo.getContext("2d").drawImage(imagen, 0, 0, lienzo.width, lienzo.height);
      URL.revokeObjectURL(url);
      lienzo.toBlob((blob) => (blob ? resolver(blob) : rechazar(new Error("No se pudo procesar la imagen."))), "image/jpeg", 0.85);
    };
    imagen.onerror = () => {
      URL.revokeObjectURL(url);
      rechazar(new Error("El archivo no es una imagen válida."));
    };
    imagen.src = url;
  });
}
